import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, Switch, TouchableOpacity } from 'react-native';
import { Stack, useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../src/context/OwnerScopeContext';
import { useToast } from '../../../src/components/Toast';
import Card from '../../../src/components/Card';
import Input from '../../../src/components/Input';
import Button from '../../../src/components/Button';
import Picker from '../../../src/components/Picker';
import ContentContainer from '../../../src/components/ContentContainer';
import InlineConfirm from '../../../src/components/InlineConfirm';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';

interface AttendantRole {
  id: string;
  name: string;
  permissions: {
    timeclock: boolean;
    start_washes: boolean;
    emergency_contact: boolean;
    refund_mode: 'none' | 'direct' | 'request';
    view_hardware: boolean;
    manage_team: boolean;
    edit_timecards: boolean;
    issue_rewards?: boolean;
    /** Round 2: can press the owner's Equipment buttons (manual Advanced actions) at assigned locations. */
    equipment_buttons?: boolean;
    /** When PRESENT, Manage Team covers attendants holding these roles (at shared locations);
     *  [] = nobody. ABSENT = legacy per-person subordinate assignment. */
    manage_team_role_ids?: string[];
    /** When PRESENT, Edit Timecards covers only shifts of attendants holding these roles;
     *  [] = nobody. ABSENT = legacy (everyone at assigned locations). */
    edit_timecards_role_ids?: string[];
  };
  sortOrder: number;
}

const DEFAULT_PERMISSIONS: AttendantRole['permissions'] = {
  timeclock: false,
  start_washes: false,
  emergency_contact: false,
  refund_mode: 'none',
  view_hardware: false,
  manage_team: false,
  edit_timecards: false,
  issue_rewards: false,
  equipment_buttons: false,
};

/** ManageRolesScreen - CRUD for attendant roles with permission management. */
export default function ManageRolesScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();

  const [roles, setRoles] = useState<AttendantRole[]>([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formVisible, setFormVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<AttendantRole | null>(null);
  const [formName, setFormName] = useState('');
  const [formPermissions, setFormPermissions] = useState<AttendantRole['permissions']>({ ...DEFAULT_PERMISSIONS });
  const [submitting, setSubmitting] = useState(false);

  // Delete state
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const toast = useToast();
  const scope = useOwnerScope();

  const fetchRoles = async () => {
    try {
      const data = await scope.api.get<AttendantRole[]>('/api/attendant-roles');
      setRoles(data);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => { fetchRoles(); }, []));

  const openCreateForm = () => {
    setEditingRole(null);
    setFormName('');
    setFormPermissions({ ...DEFAULT_PERMISSIONS });
    setFormVisible(true);
  };

  const openEditForm = (role: AttendantRole) => {
    setEditingRole(role);
    setFormName(role.name);
    setFormPermissions({ ...role.permissions });
    setFormVisible(true);
  };

  const closeForm = () => {
    setFormVisible(false);
    setEditingRole(null);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Role name is required' });
      return;
    }

    setSubmitting(true);
    try {
      if (editingRole) {
        await scope.api.patch(`/api/attendant-roles/${editingRole.id}`, {
          name: formName.trim(),
          permissions: formPermissions,
        });
        toast.show({ type: 'success', title: 'Updated', message: 'Role updated' });
      } else {
        await scope.api.post('/api/attendant-roles', {
          name: formName.trim(),
          permissions: formPermissions,
        });
        toast.show({ type: 'success', title: 'Created', message: 'Role created' });
      }
      closeForm();
      fetchRoles();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (roleId: string) => {
    setDeleting(true);
    try {
      await scope.api.delete(`/api/attendant-roles/${roleId}`);
      setConfirmDeleteId(null);
      toast.show({ type: 'success', title: 'Deleted', message: 'Role deleted' });
      fetchRoles();
    } catch (err: any) {
      setConfirmDeleteId(null);
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setDeleting(false);
    }
  };

  const togglePermission = (key: keyof Omit<AttendantRole['permissions'], 'refund_mode' | 'manage_team_role_ids' | 'edit_timecards_role_ids'>) => {
    setFormPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  /** Toggle Manage Team / Edit Timecards. First-time enable with no saved role scope defaults
   *  the list to ALL roles, so the owner narrows down from working behavior rather than
   *  silently granting nobody. */
  const toggleScopedPermission = (
    permKey: 'manage_team' | 'edit_timecards',
    idsKey: 'manage_team_role_ids' | 'edit_timecards_role_ids',
  ) => {
    setFormPermissions(prev => {
      const next = { ...prev };
      next[permKey] = !prev[permKey];
      if (next[permKey] && prev[idsKey] === undefined) {
        next[idsKey] = roles.map(r => r.id);
      }
      return next;
    });
  };

  const toggleScopeRoleId = (
    idsKey: 'manage_team_role_ids' | 'edit_timecards_role_ids',
    roleId: string,
  ) => {
    setFormPermissions(prev => {
      const current = prev[idsKey] ?? [];
      const next = { ...prev };
      next[idsKey] = current.includes(roleId)
        ? current.filter(id => id !== roleId)
        : [...current, roleId];
      return next;
    });
  };

  /** Checkbox list of the roles a scoped permission applies to. Plain render function (NOT a
   *  nested component) so rows keep identity across renders. */
  const renderRoleScope = (
    idsKey: 'manage_team_role_ids' | 'edit_timecards_role_ids',
    legacyHint: string,
  ) => {
    const selectedIds = formPermissions[idsKey];
    return (
      <View style={styles.scopeBox}>
        <Text style={styles.scopeHint}>
          {selectedIds === undefined
            ? legacyHint
            : 'Applies to attendants holding the checked roles. None checked = nobody.'}
        </Text>
        {roles.length === 0 ? (
          <Text style={styles.scopeHint}>No roles yet.</Text>
        ) : (
          roles.map(r => {
            const checked = (selectedIds ?? []).includes(r.id);
            return (
              <TouchableOpacity
                key={r.id}
                onPress={() => toggleScopeRoleId(idsKey, r.id)}
                style={styles.scopeRow}
              >
                <Ionicons
                  name={checked ? 'checkbox' : 'square-outline'}
                  size={20}
                  color={checked ? colors.primary : colors.textLight}
                />
                <Text style={styles.scopeRowLabel}>
                  {r.name}{editingRole?.id === r.id ? ' (this role)' : ''}
                </Text>
              </TouchableOpacity>
            );
          })
        )}
      </View>
    );
  };

  const permissionBadges = (perms: AttendantRole['permissions']) => {
    const badges: { label: string; color: string }[] = [];
    if (perms.timeclock) badges.push({ label: 'Timeclock', color: colors.primary });
    if (perms.start_washes) badges.push({ label: 'Start Washes', color: colors.success });
    if (perms.emergency_contact) badges.push({ label: 'Emergency', color: colors.danger });
    if (perms.view_hardware) badges.push({ label: 'Hardware', color: colors.warning });
    if (perms.manage_team) badges.push({ label: 'Manage Team', color: colors.primary });
    if (perms.edit_timecards) badges.push({ label: 'Edit Timecards', color: colors.primary });
    if (perms.refund_mode === 'direct') badges.push({ label: 'Refund: Direct', color: colors.primary });
    else if (perms.refund_mode === 'request') badges.push({ label: 'Refund: Request', color: colors.primary });
    if (perms.issue_rewards) badges.push({ label: 'Reward QRs', color: colors.success });
    if (perms.equipment_buttons) badges.push({ label: 'Equipment buttons', color: colors.warning });
    return badges;
  };

  return (
    <>
    <Stack.Screen options={{
      title: 'Manage Roles',
      headerLeft: () => (
        <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace(scope.basePath + '/staff')} hitSlop={12} style={{ padding: spacing.sm, marginLeft: -spacing.xs }}>
          <Ionicons name="arrow-back" size={28} color={colors.primary} />
        </TouchableOpacity>
      ),
    }} />
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <ContentContainer>
        <Text style={styles.title}>Manage Roles</Text>

        {!formVisible && (
          <Button
            title="Create Role"
            onPress={openCreateForm}
            icon={<Ionicons name="add-circle" size={18} color={colors.white} />}
            size="sm"
            style={{ marginBottom: spacing.lg }}
          />
        )}

        {/* Create / Edit Form */}
        {formVisible && (
          <Card style={{ marginBottom: spacing.lg }}>
            <Text style={styles.sectionTitle}>
              {editingRole ? 'Edit Role' : 'Create Role'}
            </Text>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Role Name</Text>
              <Input
                placeholder="e.g. Senior Attendant"
                value={formName}
                onChangeText={setFormName}
                autoCapitalize="words"
              />
            </View>

            <Text style={styles.formLabel}>Permissions</Text>

            <View style={styles.switchRow}>
              <View style={styles.switchLabelWrap}>
                <Ionicons name="time-outline" size={20} color={colors.primary} />
                <Text style={styles.switchLabel}>Timeclock</Text>
              </View>
              <Switch
                value={formPermissions.timeclock}
                onValueChange={() => togglePermission('timeclock')}
                trackColor={{ false: colors.borderLight, true: colors.primary + '80' }}
                thumbColor={formPermissions.timeclock ? colors.primary : colors.textLight}
              />
            </View>

            <View style={styles.switchRow}>
              <View style={styles.switchLabelWrap}>
                <Ionicons name="car-outline" size={20} color={colors.success} />
                <Text style={styles.switchLabel}>Start Washes</Text>
              </View>
              <Switch
                value={formPermissions.start_washes}
                onValueChange={() => togglePermission('start_washes')}
                trackColor={{ false: colors.borderLight, true: colors.success + '80' }}
                thumbColor={formPermissions.start_washes ? colors.success : colors.textLight}
              />
            </View>

            <View style={styles.switchRow}>
              <View style={styles.switchLabelWrap}>
                <Ionicons name="call-outline" size={20} color={colors.danger} />
                <Text style={styles.switchLabel}>Emergency Contact</Text>
              </View>
              <Switch
                value={formPermissions.emergency_contact}
                onValueChange={() => togglePermission('emergency_contact')}
                trackColor={{ false: colors.borderLight, true: colors.danger + '80' }}
                thumbColor={formPermissions.emergency_contact ? colors.danger : colors.textLight}
              />
            </View>

            <View style={styles.switchRow}>
              <View style={styles.switchLabelWrap}>
                <Ionicons name="hardware-chip-outline" size={20} color={colors.warning} />
                <Text style={styles.switchLabel}>View Hardware</Text>
              </View>
              <Switch
                value={formPermissions.view_hardware}
                onValueChange={() => togglePermission('view_hardware')}
                trackColor={{ false: colors.borderLight, true: colors.warning + '80' }}
                thumbColor={formPermissions.view_hardware ? colors.warning : colors.textLight}
              />
            </View>

            <View style={styles.switchRow}>
              <View style={styles.switchLabelWrap}>
                <Ionicons name="people-outline" size={20} color={colors.primary} />
                <Text style={styles.switchLabel}>Manage Team</Text>
              </View>
              <Switch
                value={formPermissions.manage_team}
                onValueChange={() => toggleScopedPermission('manage_team', 'manage_team_role_ids')}
                trackColor={{ false: colors.borderLight, true: colors.primary + '80' }}
                thumbColor={formPermissions.manage_team ? colors.primary : colors.textLight}
              />
            </View>

            {formPermissions.manage_team && renderRoleScope(
              'manage_team_role_ids',
              'Using per-person team assignment (set on each attendant). Checking roles below switches this role to role-based team scope.',
            )}

            <View style={styles.switchRow}>
              <View style={styles.switchLabelWrap}>
                <Ionicons name="create-outline" size={20} color={colors.primary} />
                <Text style={styles.switchLabel}>Edit Timecards</Text>
              </View>
              <Switch
                value={formPermissions.edit_timecards}
                onValueChange={() => toggleScopedPermission('edit_timecards', 'edit_timecards_role_ids')}
                trackColor={{ false: colors.borderLight, true: colors.primary + '80' }}
                thumbColor={formPermissions.edit_timecards ? colors.primary : colors.textLight}
              />
            </View>

            {formPermissions.edit_timecards && renderRoleScope(
              'edit_timecards_role_ids',
              'Currently covers all timecards at assigned locations. Checking roles below limits editing to attendants holding those roles.',
            )}

            <View style={styles.switchRow}>
              <View style={styles.switchLabelWrap}>
                <Ionicons name="gift-outline" size={20} color={colors.success} />
                <Text style={styles.switchLabel}>Generate Reward QRs (from your templates)</Text>
              </View>
              <Switch
                value={!!formPermissions.issue_rewards}
                onValueChange={() => setFormPermissions(prev => ({ ...prev, issue_rewards: !prev.issue_rewards }))}
                trackColor={{ false: colors.borderLight, true: colors.success + '80' }}
                thumbColor={formPermissions.issue_rewards ? colors.success : colors.textLight}
              />
            </View>

            <View style={styles.switchRow}>
              <View style={styles.switchLabelWrap}>
                <Ionicons name="flash-outline" size={20} color={colors.warning} />
                <Text style={styles.switchLabel}>Equipment buttons (run manual Advanced actions)</Text>
              </View>
              <Switch
                value={!!formPermissions.equipment_buttons}
                onValueChange={() => setFormPermissions(prev => ({ ...prev, equipment_buttons: !prev.equipment_buttons }))}
                trackColor={{ false: colors.borderLight, true: colors.warning + '80' }}
                thumbColor={formPermissions.equipment_buttons ? colors.warning : colors.textLight}
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Refund Mode</Text>
              <Picker
                options={[
                  { label: 'None', value: 'none' },
                  { label: 'Direct', value: 'direct' },
                  { label: 'Request Approval', value: 'request' },
                ]}
                value={formPermissions.refund_mode}
                onValueChange={(val: string) =>
                  setFormPermissions(prev => ({ ...prev, refund_mode: val as 'none' | 'direct' | 'request' }))
                }
              />
            </View>

            <View style={styles.buttonRow}>
              <Button
                title="Cancel"
                onPress={closeForm}
                variant="outline"
                style={styles.halfButton}
              />
              <Button
                title={editingRole ? 'Update Role' : 'Create Role'}
                onPress={handleSave}
                loading={submitting}
                style={styles.halfButton}
              />
            </View>
          </Card>
        )}

        {/* Role List */}
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : roles.length === 0 && !formVisible ? (
          <Card style={styles.emptyCard}>
            <Ionicons name="shield-outline" size={64} color={colors.textLight} />
            <Text style={styles.emptyTitle}>No Roles</Text>
            <Text style={styles.emptyText}>Create roles to define permissions for your staff</Text>
            <Button
              title="Create First Role"
              onPress={openCreateForm}
              style={{ marginTop: spacing.lg }}
            />
          </Card>
        ) : (
          roles.map(role => {
            const badges = permissionBadges(role.permissions);
            return (
              <Card key={role.id} style={styles.roleCard}>
                <View style={styles.roleHeader}>
                  <View style={styles.roleIconWrap}>
                    <Ionicons name="shield-checkmark" size={24} color={colors.primary} />
                  </View>
                  <Text style={styles.roleName}>{role.name}</Text>
                </View>

                {badges.length > 0 ? (
                  <View style={styles.badgeRow}>
                    {badges.map(b => (
                      <View key={b.label} style={[styles.badge, { backgroundColor: b.color + '20' }]}>
                        <Text style={[styles.badgeText, { color: b.color }]}>{b.label}</Text>
                      </View>
                    ))}
                  </View>
                ) : (
                  <Text style={styles.noBadgesText}>No permissions enabled</Text>
                )}

                <View style={styles.roleActions}>
                  <Button
                    title="Edit"
                    onPress={() => openEditForm(role)}
                    variant="outline"
                    size="sm"
                    icon={<Ionicons name="pencil" size={14} color={colors.primary} />}
                    style={styles.actionButton}
                  />
                  <Button
                    title="Delete"
                    onPress={() => setConfirmDeleteId(role.id)}
                    variant="danger"
                    size="sm"
                    icon={<Ionicons name="trash" size={14} color={colors.white} />}
                    style={styles.actionButton}
                  />
                </View>
                <InlineConfirm
                  message={`Delete role "${role.name}"? Attendants using this role will need to be reassigned.`}
                  visible={confirmDeleteId === role.id}
                  onConfirm={() => handleDelete(role.id)}
                  onCancel={() => setConfirmDeleteId(null)}
                  confirmLabel="Delete"
                  loading={deleting}
                  variant="danger"
                />
              </Card>
            );
          })
        )}

        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: c.background,
  },
  scrollContent: {
    padding: spacing.md,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.xxl,
  },
  title: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginBottom: spacing.md,
  },
  formGroup: {
    marginBottom: spacing.md,
  },
  formLabel: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: c.text,
    marginBottom: spacing.sm,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    backgroundColor: c.surface,
    borderRadius: borderRadius.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: c.borderLight,
  },
  switchLabelWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    // Long labels wrap instead of shoving the Switch off-screen.
    flex: 1,
    paddingRight: spacing.sm,
  },
  switchLabel: {
    fontSize: fontSize.md,
    color: c.text,
    flexShrink: 1,
  },
  // Role-scope checkbox list (Manage Team / Edit Timecards)
  scopeBox: {
    marginLeft: spacing.lg,
    marginBottom: spacing.sm,
    padding: spacing.sm,
    backgroundColor: c.surface,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: c.borderLight,
  },
  scopeHint: {
    fontSize: fontSize.xs,
    color: c.textSecondary,
    marginBottom: spacing.sm,
  },
  scopeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  scopeRowLabel: {
    fontSize: fontSize.sm,
    color: c.text,
    marginLeft: spacing.sm,
    flexShrink: 1,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.lg,
  },
  halfButton: {
    flex: 1,
  },
  // Empty state
  emptyCard: {
    alignItems: 'center',
    paddingVertical: spacing.xxl,
  },
  emptyTitle: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginTop: spacing.md,
  },
  emptyText: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    textAlign: 'center',
    marginTop: spacing.sm,
    maxWidth: 280,
  },
  // Role cards
  roleCard: {
    marginBottom: spacing.md,
  },
  roleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  roleIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: c.primaryBg,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  roleName: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.bold,
    color: c.text,
    flex: 1,
  },
  badgeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginBottom: spacing.md,
  },
  badge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: 12,
  },
  badgeText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
  },
  noBadgesText: {
    fontSize: fontSize.sm,
    color: c.textLight,
    marginBottom: spacing.md,
  },
  roleActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  actionButton: {
    flex: 1,
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
